import { Link } from 'react-router-dom';

interface Step {
  id: number;
  label: string;
  week: string;
  color: string;
}

interface CostItem {
  name: string;
  unit: string;
  priceMin: number;
  priceMax?: number;
  note: string;
}

interface CostCategory {
  category: string;
  icon: string;
  color: string;
  items: CostItem[];
}

interface StepCostData {
  titleEn: string;
  title: string;
  subtitle: string;
  notice: string[];
  categories: CostCategory[];
}

interface Props {
  activeStep: number;
  steps: Step[];
}

const STEP_COSTS: Record<number, StepCostData> = {
  1: {
    titleEn: 'Demolition Unit Cost',
    title: '철거 공정 단품 비용 안내',
    subtitle: '항목별 단품 철거 기준 단가 (시공비 기준, 폐기물 처리비 별도)',
    notice: [
      '위 단가는 시공비 기준이며, 폐기물 처리비는 별도 산정됩니다.',
      '비내력벽 철거는 반드시 구조 전문가 확인 후 진행하며, 관리사무소 사전 신고가 필수입니다.',
      '현장 상황(접착 방식, 층수, 자재 두께 등)에 따라 실제 비용이 달라질 수 있습니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '바닥재 철거',
        icon: 'ri-layout-bottom-line',
        color: '#8B7355',
        items: [
          { name: '강마루 / 원목마루 철거', unit: '평', priceMin: 3, priceMax: 5, note: '접착제 제거 포함' },
          { name: '장판 철거', unit: '평', priceMin: 1, priceMax: 2, note: '하지 상태에 따라 상이' },
          { name: '타일 바닥 철거', unit: '평', priceMin: 5, priceMax: 8, note: '두께·접착 방식에 따라 상이' },
          { name: '데코타일 철거', unit: '평', priceMin: 2, priceMax: 4, note: '접착제 잔여물 제거 포함' },
        ],
      },
      {
        category: '벽체 · 천장 철거',
        icon: 'ri-building-line',
        color: '#6B5B45',
        items: [
          { name: '도배 제거 (합지)', unit: '평', priceMin: 1, priceMax: 2, note: '초배지 포함' },
          { name: '도배 제거 (실크)', unit: '평', priceMin: 2, priceMax: 3, note: '풀 제거 포함' },
          { name: '석고보드 벽체 철거', unit: '평', priceMin: 3, priceMax: 5, note: '경량 스터드 포함' },
          { name: '타일 벽체 철거', unit: '평', priceMin: 6, priceMax: 10, note: '욕실·주방 벽 기준' },
          { name: '천장 마감재 철거', unit: '평', priceMin: 3, priceMax: 6, note: '텍스·석고보드 포함' },
        ],
      },
      {
        category: '욕실 · 주방 철거',
        icon: 'ri-drop-line',
        color: '#4A7C8E',
        items: [
          { name: '욕실 전체 철거 (소형)', unit: '개소', priceMin: 60, priceMax: 100, note: '1.5평 기준, 도기·타일 포함' },
          { name: '욕실 전체 철거 (중형)', unit: '개소', priceMin: 100, priceMax: 150, note: '2~3평 기준' },
          { name: '주방 싱크대 철거', unit: '식', priceMin: 30, priceMax: 60, note: '상·하부장 포함' },
          { name: '주방 타일 철거', unit: '평', priceMin: 6, priceMax: 10, note: '뒷벽 타일 기준' },
        ],
      },
      {
        category: '창호 · 문 철거',
        icon: 'ri-door-open-line',
        color: '#7B6B8B',
        items: [
          { name: '시스템창 철거 (소형)', unit: '개소', priceMin: 10, priceMax: 20, note: '1.2m 이하 기준' },
          { name: '시스템창 철거 (대형)', unit: '개소', priceMin: 20, priceMax: 40, note: '1.2m 초과 기준' },
          { name: '현관문 철거', unit: '개소', priceMin: 20, priceMax: 40, note: '방화문 포함' },
          { name: '실내문 철거', unit: '개소', priceMin: 5, priceMax: 10, note: '문틀 포함' },
        ],
      },
      {
        category: '구조 · 비내력벽 철거',
        icon: 'ri-hammer-line',
        color: '#9B4444',
        items: [
          { name: '비내력벽 철거 (경량)', unit: '개소', priceMin: 30, priceMax: 60, note: '석고보드 벽체 기준' },
          { name: '비내력벽 철거 (조적)', unit: '개소', priceMin: 60, priceMax: 120, note: '벽돌·블록 벽체 기준' },
          { name: '문 개구부 확장', unit: '개소', priceMin: 40, priceMax: 80, note: '인방 보강 포함' },
          { name: '구조 보강 (인방)', unit: '개소', priceMin: 50, priceMax: 100, note: '철골 인방 설치 기준' },
        ],
      },
      {
        category: '폐기물 처리',
        icon: 'ri-delete-bin-line',
        color: '#6B8E6B',
        items: [
          { name: '폐기물 처리 (20평 이하)', unit: '식', priceMin: 30, priceMax: 50, note: '1톤 트럭 1~2대 기준' },
          { name: '폐기물 처리 (30평)', unit: '식', priceMin: 50, priceMax: 80, note: '1톤 트럭 2~3대 기준' },
          { name: '폐기물 처리 (40평 이상)', unit: '식', priceMin: 80, priceMax: 130, note: '현장 상황에 따라 상이' },
          { name: '엘리베이터 보양 설치', unit: '식', priceMin: 10, priceMax: 20, note: '공사 기간 전체 기준' },
        ],
      },
    ],
  },
  2: {
    titleEn: 'Plumbing & Waterproofing Cost',
    title: '설비 · 배관 공정 단품 비용 안내',
    subtitle: '항목별 설비·배관 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '보일러 교체는 가스 기술자 자격증 보유자가 시공하며, 도시가스 회사 신고가 필요합니다.',
      '방수 공사는 양생 기간(최소 24시간) 준수 후 다음 공정을 진행합니다.',
      '배관 교체 시 벽체 내부 작업이 포함될 경우 목공 공정과 연계됩니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '보일러 · 난방',
        icon: 'ri-fire-line',
        color: '#B8860B',
        items: [
          { name: '콘덴싱 보일러 교체 (소형)', unit: '대', priceMin: 80, priceMax: 120, note: '13~16kW, 자재+시공 포함' },
          { name: '콘덴싱 보일러 교체 (대형)', unit: '대', priceMin: 130, priceMax: 200, note: '20kW 이상, 대형 평수용' },
          { name: '온수 분배기 교체', unit: '식', priceMin: 30, priceMax: 60, note: '방별 밸브 포함' },
          { name: '난방 배관 전체 교체', unit: '평', priceMin: 8, priceMax: 14, note: 'PB관 기준' },
          { name: '온돌 패드 (전기 난방)', unit: '평', priceMin: 10, priceMax: 18, note: '전기 온돌 시공 기준' },
        ],
      },
      {
        category: '급수 · 배수 배관',
        icon: 'ri-drop-line',
        color: '#4A7C8E',
        items: [
          { name: '급수 배관 전체 교체', unit: '평', priceMin: 5, priceMax: 10, note: 'PB관 기준, 욕실·주방 포함' },
          { name: '배수 배관 교체', unit: '개소', priceMin: 30, priceMax: 70, note: '욕실 1개소 기준' },
          { name: '세탁기 배수 라인 추가', unit: '개소', priceMin: 15, priceMax: 30, note: '신규 라인 설치' },
          { name: '주방 배수 라인 교체', unit: '개소', priceMin: 20, priceMax: 40, note: '싱크대 하부 포함' },
        ],
      },
      {
        category: '방수 공사',
        icon: 'ri-shield-check-line',
        color: '#6B8E6B',
        items: [
          { name: '욕실 방수 (소형)', unit: '개소', priceMin: 40, priceMax: 70, note: '1.5평 기준, 우레탄 2중 방수' },
          { name: '욕실 방수 (중형)', unit: '개소', priceMin: 70, priceMax: 110, note: '2~3평 기준' },
          { name: '발코니 방수', unit: '개소', priceMin: 30, priceMax: 60, note: '우레탄 방수 기준' },
          { name: '지붕·옥상 방수', unit: '평', priceMin: 8, priceMax: 15, note: '우레탄·시트 방수 선택' },
        ],
      },
      {
        category: '위생기기 설치',
        icon: 'ri-home-gear-line',
        color: '#8B7355',
        items: [
          { name: '양변기 교체', unit: '개', priceMin: 15, priceMax: 30, note: '자재비 별도, 시공비 기준' },
          { name: '세면대 교체', unit: '개', priceMin: 15, priceMax: 25, note: '수전 포함, 자재비 별도' },
          { name: '욕조 교체', unit: '개', priceMin: 30, priceMax: 60, note: '아크릴 욕조 기준' },
          { name: '샤워 부스 설치', unit: '개소', priceMin: 50, priceMax: 120, note: '프레임리스 강화유리 기준' },
          { name: '수전 교체 (욕실)', unit: '개', priceMin: 8, priceMax: 20, note: '자재비 별도, 시공비 기준' },
        ],
      },
    ],
  },
  3: {
    titleEn: 'Carpentry & Window Cost',
    title: '목공 · 창호 공정 단품 비용 안내',
    subtitle: '항목별 목공·창호 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '창호 교체는 관리사무소 사전 신고 필수이며, 발주 후 제작 기간(7~10일)이 소요됩니다.',
      '붙박이장은 현장 실측 후 맞춤 제작되며, 제작 기간 약 2~3주가 필요합니다.',
      '목공 단계에서 전기 콘센트·스위치 위치를 전기 기사와 함께 확정해야 합니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '벽체 · 천장 목공',
        icon: 'ri-tools-line',
        color: '#6B8E6B',
        items: [
          { name: '경량 벽체 신설', unit: '평', priceMin: 8, priceMax: 14, note: '석고보드 2겹 기준' },
          { name: '천장 석고보드 시공', unit: '평', priceMin: 6, priceMax: 10, note: '경량 틀 포함' },
          { name: '단열재 시공 (내벽)', unit: '평', priceMin: 4, priceMax: 8, note: '글라스울·압출법 선택' },
          { name: '방음재 시공', unit: '평', priceMin: 6, priceMax: 12, note: '층간·세대간 방음' },
        ],
      },
      {
        category: '붙박이장 · 수납',
        icon: 'ri-archive-line',
        color: '#8B7355',
        items: [
          { name: '붙박이장 (2400H 기준)', unit: '개소', priceMin: 100, priceMax: 200, note: '폭 1.2m 기준, 맞춤 제작' },
          { name: '드레스룸 전체 시공', unit: '식', priceMin: 200, priceMax: 400, note: '3~4평 기준' },
          { name: '팬트리 수납장', unit: '개소', priceMin: 80, priceMax: 150, note: '주방 인접 수납 공간' },
          { name: '신발장 맞춤 제작', unit: '개소', priceMin: 60, priceMax: 120, note: '현관 맞춤 기준' },
        ],
      },
      {
        category: '시스템창 교체',
        icon: 'ri-window-line',
        color: '#7B6B8B',
        items: [
          { name: '일반 이중창', unit: '개소', priceMin: 40, priceMax: 70, note: '기본 단열 성능' },
          { name: '로이유리 이중창', unit: '개소', priceMin: 60, priceMax: 100, note: '단열·자외선 차단 강화' },
          { name: '방음 삼중창', unit: '개소', priceMin: 100, priceMax: 160, note: '방음 최고 등급, 도로변 추천' },
          { name: '발코니 확장 창호', unit: '개소', priceMin: 80, priceMax: 140, note: '대형 픽스창 포함' },
        ],
      },
      {
        category: '현관문 · 실내문',
        icon: 'ri-door-open-line',
        color: '#9B4444',
        items: [
          { name: '현관 방화문 교체', unit: '개소', priceMin: 120, priceMax: 200, note: '스마트 도어락 포함' },
          { name: '실내문 세트 교체', unit: '개', priceMin: 25, priceMax: 50, note: '문틀·문짝·하드웨어 포함' },
          { name: '실내 중문 (슬라이딩)', unit: '개소', priceMin: 60, priceMax: 120, note: '강화유리·우드 선택' },
          { name: '포켓도어 설치', unit: '개소', priceMin: 100, priceMax: 180, note: '벽 매립형, 공간 절약' },
          { name: '중문 (여닫이형)', unit: '개소', priceMin: 50, priceMax: 100, note: '단열·방음 효과' },
        ],
      },
    ],
  },
  4: {
    titleEn: 'Electrical & Lighting Cost',
    title: '전기 · 조명 공정 단품 비용 안내',
    subtitle: '항목별 전기·조명 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '전기 공사는 반드시 전기 기술자 자격증 보유자가 시공합니다.',
      '분전반 용량 및 회로 수는 사전에 확인 후 결정합니다.',
      '스마트홈 시스템은 인터넷 환경 및 기기 호환성 사전 확인이 필요합니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '전기 배선 · 분전반',
        icon: 'ri-flashlight-line',
        color: '#B8860B',
        items: [
          { name: '전기 배선 전체 교체', unit: '평', priceMin: 8, priceMax: 14, note: '분전반 포함, 30평 기준' },
          { name: '분전반 교체 (20회로)', unit: '대', priceMin: 30, priceMax: 60, note: '누전차단기 포함' },
          { name: '분전반 교체 (30회로)', unit: '대', priceMin: 50, priceMax: 90, note: '대형 평수용' },
          { name: '콘센트 추가 설치', unit: '개', priceMin: 3, priceMax: 6, note: '배선 연장 포함' },
          { name: '전기 용량 증설', unit: '식', priceMin: 50, priceMax: 120, note: '한전 신청 포함' },
        ],
      },
      {
        category: '조명 기구 설치',
        icon: 'ri-lightbulb-line',
        color: '#c9a84c',
        items: [
          { name: '매입형 LED 다운라이트', unit: '개', priceMin: 2, priceMax: 4, note: '6인치 15W 기준, 시공비' },
          { name: '간접 조명 (코브 조명)', unit: 'm', priceMin: 3, priceMax: 6, note: 'LED 바 + 몰딩 포함' },
          { name: '펜던트 조명 설치', unit: '개', priceMin: 3, priceMax: 8, note: '배선 연결 시공비' },
          { name: '욕실 방습 조명', unit: '개', priceMin: 3, priceMax: 6, note: 'IP44 이상 방습 등급' },
          { name: '주방 하부장 조명', unit: '식', priceMin: 10, priceMax: 20, note: 'LED 바 + 센서 포함' },
        ],
      },
      {
        category: '스위치 · 콘센트',
        icon: 'ri-plug-line',
        color: '#6B8E6B',
        items: [
          { name: '스위치 전체 교체', unit: '평', priceMin: 1, priceMax: 2, note: '일반 스위치 기준' },
          { name: '터치 스위치 교체', unit: '개', priceMin: 3, priceMax: 8, note: '자재비 포함' },
          { name: '스마트 스위치 설치', unit: '개', priceMin: 5, priceMax: 12, note: '와이파이 연동형' },
          { name: 'USB 콘센트 설치', unit: '개', priceMin: 3, priceMax: 6, note: 'USB-A/C 포트 포함' },
        ],
      },
      {
        category: '스마트홈 시스템',
        icon: 'ri-home-wifi-line',
        color: '#4A7C8E',
        items: [
          { name: '스마트홈 기본 패키지', unit: '식', priceMin: 80, priceMax: 150, note: '조명·난방 제어 포함' },
          { name: '스마트홈 풀 패키지', unit: '식', priceMin: 150, priceMax: 300, note: '조명·난방·보안·가전 연동' },
          { name: '월패드 교체', unit: '대', priceMin: 30, priceMax: 80, note: '인터폰·난방 통합 제어' },
          { name: 'CCTV 설치 (실내)', unit: '개', priceMin: 10, priceMax: 25, note: '와이파이 카메라 기준' },
        ],
      },
    ],
  },
  5: {
    titleEn: 'Tile & Flooring Cost',
    title: '타일 · 바닥 공정 단품 비용 안내',
    subtitle: '항목별 타일·바닥재 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '타일 시공 후 최소 24시간 양생이 필요하며, 줄눈 시공 후 48시간 이상 건조 후 사용을 권장합니다.',
      '강마루는 시공 전 자재를 현장에서 24시간 이상 보관 후 시공합니다.',
      '헤링본·패턴 시공은 일반 시공 대비 자재 손실률이 높아 추가 비용이 발생합니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '욕실 타일',
        icon: 'ri-drop-line',
        color: '#4A7C8E',
        items: [
          { name: '욕실 타일 전체 시공 (소형)', unit: '개소', priceMin: 80, priceMax: 130, note: '1.5평, 바닥+벽 포함' },
          { name: '욕실 타일 전체 시공 (중형)', unit: '개소', priceMin: 130, priceMax: 200, note: '2~3평 기준' },
          { name: '포세린 타일 (600×1200)', unit: '평', priceMin: 15, priceMax: 25, note: '대형 타일 시공비 포함' },
          { name: '모자이크 타일 포인트', unit: '평', priceMin: 20, priceMax: 35, note: '줄눈 포함' },
        ],
      },
      {
        category: '주방 · 발코니 타일',
        icon: 'ri-restaurant-line',
        color: '#8B7355',
        items: [
          { name: '주방 뒷벽 타일 시공', unit: '평', priceMin: 12, priceMax: 20, note: '헤링본 패턴 포함' },
          { name: '발코니 타일 시공', unit: '평', priceMin: 10, priceMax: 18, note: '미끄럼 방지 타일 기준' },
          { name: '현관 타일 시공', unit: '평', priceMin: 12, priceMax: 22, note: '포세린·천연석 선택' },
          { name: '타일 줄눈 재시공', unit: '평', priceMin: 3, priceMax: 6, note: '에폭시 줄눈 기준' },
        ],
      },
      {
        category: '거실 · 방 바닥재',
        icon: 'ri-layout-bottom-line',
        color: '#6B5B45',
        items: [
          { name: '강마루 시공', unit: '평', priceMin: 10, priceMax: 15, note: '자재비 포함, 오크 기준' },
          { name: '원목마루 시공', unit: '평', priceMin: 18, priceMax: 30, note: '자재비 포함, 고급형' },
          { name: '헤링본 마루 시공', unit: '평', priceMin: 18, priceMax: 28, note: '패턴 시공 추가비 포함' },
          { name: '장판 시공', unit: '평', priceMin: 5, priceMax: 8, note: '방 전용, 자재비 포함' },
          { name: '데코타일 시공', unit: '평', priceMin: 6, priceMax: 10, note: '사무실·다용도실 추천' },
        ],
      },
      {
        category: '하지 · 레벨링 작업',
        icon: 'ri-tools-line',
        color: '#6B8E6B',
        items: [
          { name: '셀프 레벨링 (바닥 평탄화)', unit: '평', priceMin: 3, priceMax: 6, note: '단차 10mm 이하 기준' },
          { name: '방습 처리', unit: '평', priceMin: 2, priceMax: 4, note: '1층·지하 필수' },
          { name: '걸레받이 설치', unit: 'm', priceMin: 0.5, priceMax: 1, note: 'MDF·PVC 선택' },
          { name: '문틀 하부 컷팅', unit: '개', priceMin: 1, priceMax: 2, note: '바닥재 두께 조정' },
        ],
      },
    ],
  },
  6: {
    titleEn: 'Painting & Wallpaper Cost',
    title: '도장 · 도배 공정 단품 비용 안내',
    subtitle: '항목별 도장·도배 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '도배 후 최소 2~3일 환기가 필수이며, 페인트 도장 시 VOC 저함량 친환경 제품을 사용합니다.',
      '스터코·마이크로시멘트 등 고급 질감 마감은 시공 기간이 일반 도배보다 길어집니다.',
      '기존 벽지 상태에 따라 초배지 추가 비용이 발생할 수 있습니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '도배 (벽지)',
        icon: 'ri-paint-brush-line',
        color: '#9B7B6B',
        items: [
          { name: '합지 도배 (전체)', unit: '평', priceMin: 4, priceMax: 6, note: '자재비 포함, 30평 기준' },
          { name: '실크 도배 (전체)', unit: '평', priceMin: 7, priceMax: 10, note: '고급 실크 벽지 기준' },
          { name: '천연 소재 벽지', unit: '평', priceMin: 12, priceMax: 20, note: '황토·한지·삼베 등' },
          { name: '포인트 벽지 시공', unit: '면', priceMin: 15, priceMax: 30, note: '1개 면 기준, 자재비 포함' },
          { name: '초배지 추가 시공', unit: '평', priceMin: 1, priceMax: 2, note: '기존 벽 상태 불량 시' },
        ],
      },
      {
        category: '페인트 도장',
        icon: 'ri-drop-fill',
        color: '#6B8E6B',
        items: [
          { name: '벽 페인트 도장 (전체)', unit: '평', priceMin: 6, priceMax: 9, note: '친환경 수성 페인트' },
          { name: '천장 페인트 도장', unit: '평', priceMin: 4, priceMax: 7, note: '화이트 무광 기준' },
          { name: '문틀·몰딩 도장', unit: '식', priceMin: 20, priceMax: 40, note: '30평 전체 기준' },
          { name: '외부 발코니 도장', unit: '평', priceMin: 5, priceMax: 9, note: '방수 페인트 기준' },
        ],
      },
      {
        category: '고급 질감 마감',
        icon: 'ri-artboard-line',
        color: '#8B7355',
        items: [
          { name: '스터코 마감', unit: '평', priceMin: 12, priceMax: 18, note: '유럽식 질감 마감' },
          { name: '마이크로시멘트', unit: '평', priceMin: 20, priceMax: 35, note: '욕실·주방 적용 가능' },
          { name: '벽돌 패턴 마감', unit: '평', priceMin: 10, priceMax: 18, note: '인테리어 벽돌 시트' },
          { name: '우드 패널 시공', unit: '평', priceMin: 15, priceMax: 28, note: '포인트 벽 우드 마감' },
        ],
      },
      {
        category: '하지 · 퍼티 작업',
        icon: 'ri-brush-line',
        color: '#4A7C8E',
        items: [
          { name: '퍼티 작업 (전체)', unit: '평', priceMin: 2, priceMax: 4, note: '도배·도장 전 하지 처리' },
          { name: '균열 보수', unit: '개소', priceMin: 3, priceMax: 8, note: '크랙 충전 및 보강' },
          { name: '곰팡이 제거 처리', unit: '평', priceMin: 3, priceMax: 6, note: '항균 처리 포함' },
          { name: '결로 방지 단열 시공', unit: '평', priceMin: 8, priceMax: 15, note: '외벽 접한 벽면 기준' },
        ],
      },
    ],
  },
  7: {
    titleEn: 'Furniture & Finishing Cost',
    title: '가구 · 마감 공정 단품 비용 안내',
    subtitle: '항목별 가구·마감 기준 단가 (자재비 + 시공비 포함)',
    notice: [
      '주방 싱크대·욕실 가구는 현장 실측 후 맞춤 제작되며, 제작 기간 약 2~3주가 필요합니다.',
      '빌트인 가전은 사전에 치수 및 전기 용량을 확인 후 발주합니다.',
      '마감 공정은 모든 공정이 완료된 후 진행하며, 청소 및 최종 점검이 포함됩니다.',
      '정확한 견적은 현장 방문 후 제공됩니다.',
    ],
    categories: [
      {
        category: '주방 가구',
        icon: 'ri-restaurant-line',
        color: '#7A6E5F',
        items: [
          { name: '싱크대 전체 교체 (일자형)', unit: '식', priceMin: 200, priceMax: 350, note: '2400mm 기준, 상판 포함' },
          { name: '싱크대 전체 교체 (ㄱ자형)', unit: '식', priceMin: 300, priceMax: 500, note: '2700mm 이상 기준' },
          { name: '상판만 교체 (세라믹)', unit: '식', priceMin: 80, priceMax: 150, note: '기존 하부장 유지' },
          { name: '상판만 교체 (석영석)', unit: '식', priceMin: 60, priceMax: 120, note: '기존 하부장 유지' },
          { name: '주방 수전 교체', unit: '개', priceMin: 10, priceMax: 25, note: '자재비 별도, 시공비' },
        ],
      },
      {
        category: '욕실 가구 · 위생기기',
        icon: 'ri-drop-line',
        color: '#4A7C8E',
        items: [
          { name: '욕실 세면대 세트', unit: '개소', priceMin: 40, priceMax: 80, note: '수전·거울장 포함' },
          { name: '양변기 교체', unit: '개', priceMin: 30, priceMax: 70, note: '자재비 포함, 절수형' },
          { name: '욕실 수납장 (거울장)', unit: '개', priceMin: 20, priceMax: 50, note: '매립형·부착형 선택' },
          { name: '욕실 액세서리 세트', unit: '식', priceMin: 10, priceMax: 25, note: '수건걸이·휴지걸이 등' },
        ],
      },
      {
        category: '현관 · 도어 하드웨어',
        icon: 'ri-door-lock-line',
        color: '#9B4444',
        items: [
          { name: '스마트 도어락 설치', unit: '개', priceMin: 20, priceMax: 50, note: '자재비 별도, 시공비' },
          { name: '실내문 손잡이 교체', unit: '개', priceMin: 3, priceMax: 8, note: '자재비 포함' },
          { name: '경첩 교체', unit: '개', priceMin: 1, priceMax: 3, note: '스테인리스 기준' },
          { name: '도어 스토퍼 설치', unit: '개', priceMin: 0.5, priceMax: 1, note: '바닥·벽 부착형' },
        ],
      },
      {
        category: '마감 · 청소',
        icon: 'ri-sparkling-line',
        color: '#6B8E6B',
        items: [
          { name: '입주 청소 (30평)', unit: '식', priceMin: 30, priceMax: 60, note: '전문 업체 기준' },
          { name: '입주 청소 (40평 이상)', unit: '식', priceMin: 50, priceMax: 90, note: '전문 업체 기준' },
          { name: '몰딩 마감 전체', unit: '평', priceMin: 2, priceMax: 4, note: '걸레받이·천장 몰딩 포함' },
          { name: '실리콘 코킹 마감', unit: '식', priceMin: 10, priceMax: 25, note: '욕실·주방·창호 전체' },
          { name: '하자 보수 (2년 A/S)', unit: '식', priceMin: 0, note: '필그린 시공 시 무상 제공' },
        ],
      },
    ],
  },
};

function CostTable({ data, stepLabel }: { data: StepCostData; stepLabel: string }) {
  return (
    <div>
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <span className="inline-block text-xs font-semibold tracking-[0.3em] uppercase mb-2" style={{ color: '#c9a84c' }}>
            {data.titleEn}
          </span>
          <h2 className="text-xl md:text-3xl font-bold text-[#2a2218] whitespace-nowrap">{data.title}</h2>
          <p className="text-[#8a7a6a] text-xs md:text-sm mt-1 whitespace-nowrap">{data.subtitle}</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold shrink-0" style={{ backgroundColor: '#c9a84c22', color: '#c9a84c', border: '1px solid #c9a84c44' }}>
          <i className="ri-information-line text-xs" />
          <span>VAT 별도 · 현장 상황에 따라 변동</span>
        </div>
      </div>

      {/* Cost Categories */}
      <div className="space-y-5">
        {data.categories.map((cat) => (
          <div key={cat.category} className="rounded-2xl overflow-hidden border border-[#e8e0d0]" style={{ backgroundColor: '#ffffff' }}>
            {/* Category Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[#e8e0d0]" style={{ backgroundColor: `${cat.color}10` }}>
              <div className="w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0" style={{ backgroundColor: `${cat.color}20` }}>
                <i className={`${cat.icon} text-base`} style={{ color: cat.color }} />
              </div>
              <h3 className="text-[#2a2218] font-semibold text-sm">{cat.category}</h3>
              <span className="ml-auto text-xs px-2.5 py-1 rounded-full font-medium" style={{ backgroundColor: `${cat.color}15`, color: cat.color }}>
                {cat.items.length}개 항목
              </span>
            </div>

            {/* Items Table */}
            <div className="divide-y divide-[#f0ebe0]">
              {/* Table Header */}
              <div className="grid grid-cols-12 px-5 py-2.5 text-[#9e8e7e] text-xs font-medium">
                <span className="col-span-5">항목명</span>
                <span className="col-span-2 text-center">단위</span>
                <span className="col-span-3 text-right">단가 (만원)</span>
                <span className="col-span-2 text-right">비고</span>
              </div>
              {cat.items.map((item, idx) => (
                <div key={item.name} className={`grid grid-cols-12 px-5 py-3.5 items-center transition-colors hover:bg-[#faf7f2] ${idx % 2 !== 0 ? 'bg-[#fdf9f4]' : ''}`}>
                  <span className="col-span-5 text-[#2a2218] text-sm font-medium leading-snug pr-2">{item.name}</span>
                  <span className="col-span-2 text-center">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#f0ebe0', color: '#8a7a6a' }}>{item.unit}</span>
                  </span>
                  <span className="col-span-3 text-right">
                    <span className="font-bold text-sm" style={{ color: '#c9a84c' }}>
                      {item.priceMin === 0 ? '무상' : `${item.priceMin.toLocaleString()}${item.priceMax ? `~${item.priceMax.toLocaleString()}` : ''}`}
                    </span>
                    {item.priceMin > 0 && <span className="text-[#9e8e7e] text-xs ml-1">만원</span>}
                  </span>
                  <span className="col-span-2 text-right text-[#9e8e7e] text-xs leading-snug pl-2">{item.note}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Notice Banner */}
      <div className="mt-6 rounded-2xl p-5 flex flex-col md:flex-row items-start md:items-center gap-4" style={{ backgroundColor: '#eeece9', border: '1px solid #c9a84c33' }}>
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#c9a84c22' }}>
            <i className="ri-alert-line text-lg" style={{ color: '#c9a84c' }} />
          </div>
          <div>
            <p className="text-[#2a2218] font-semibold text-sm mb-1.5">{stepLabel} 공정 안내 사항</p>
            <ul className="text-[#6b5e4e] text-xs leading-relaxed space-y-1">
              {data.notice.map((n) => (<li key={n} className="break-keep">· {n}</li>))}
            </ul>
          </div>
        </div>
        <Link to="/estimate" className="bg-[#c9a84c] text-[#0d0d0d] text-sm font-bold px-6 py-3 rounded-full whitespace-nowrap cursor-pointer hover:bg-[#b8973b] transition-colors shrink-0">
          견적 문의하기
        </Link>
      </div>
    </div>
  );
}

export default function ProcessProducts({ activeStep, steps }: Props) {
  const step = steps.find((s) => s.id === activeStep);
  const costData = STEP_COSTS[activeStep];

  if (costData && step) {
    return <CostTable data={costData} stepLabel={step.label} />;
  }

  return null;
}
